import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';
import * as styles from './UserGroupsSettings.module.scss';

const SETTING_FRAGMENT = gql(`
  fragment UserGroupsSettings_settings on OrganizationSettings {
    allowUserGroupCreation
  }
`);

const UPDATE_USER_GROUPS_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateUserGroupsSettings($allowUserGroupCreation: Boolean!) {
    organization: updateOrganization(settings: { allowUserGroupCreation: $allowUserGroupCreation }) {
      id
      settings {
        ...UserGroupsSettings_settings
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Users can directly create new user groups',
  disabled: 'New groups must be requested and approved by an admin',
} as const;

type Values = {
  allowUserGroupCreation: keyof typeof OPTIONS;
};

interface UserGroupsSettingsProps {
  settings: DocumentType<typeof SETTING_FRAGMENT>;
}

const UserGroupsSettings = ({ settings }: UserGroupsSettingsProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateUserGroupsSettings] = useMutation(
    UPDATE_USER_GROUPS_SETTINGS_MUTATION,
  );

  const allowUserGroupCreation = settings.allowUserGroupCreation
    ? 'enabled'
    : 'disabled';

  const handleSubmit = async (values: Values) => {
    await updateUserGroupsSettings({
      variables: {
        allowUserGroupCreation: values.allowUserGroupCreation === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return isEditing ? (
    <SettingCard
      title="User groups"
      description={
        <Form<Values>
          initialValues={{ allowUserGroupCreation }}
          onSubmit={handleSubmit}
        >
          <div className={styles.formFields}>
            {(['disabled', 'enabled'] as const).map((value) => (
              <Field
                key={value}
                label={OPTIONS[value]}
                name="allowUserGroupCreation"
                type="radio"
                value={value}
              />
            ))}
          </div>
          <div className={styles.description}>
            Regardless of selection, guests cannot create or request groups.
          </div>
          <div className={styles.formFooter}>
            <Submit variant="primary">Save</Submit>
          </div>
        </Form>
      }
    />
  ) : (
    <SettingCard
      actions={
        <Button onClick={() => setIsEditing(true)} variant="secondary">
          Edit
        </Button>
      }
      title="User groups"
      description={OPTIONS[allowUserGroupCreation]}
    />
  );
};

export default UserGroupsSettings;
